import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import api from "../../../api/api.js";
import OrderTimeline from "../../../components/orders/OrderTimeline.jsx";
import AddItemModal from "../../../components/orders/AddItemModal.jsx";

export default function EmployeeOrderDetailsPage() {
    const { id } = useParams();

    const [order, setOrder] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddItem, setShowAddItem] = useState(false);
    const [error, setError] = useState("");

    const loadOrder = async () => {
        try {
            setError("");

            const orderRes = await api.get(`/orders/${id}`);
            const loadedOrder = orderRes.data;

            const shipmentId =
                loadedOrder?.shipment?.id ||
                loadedOrder?.shipmentId ||
                id;

            const historyRes = await api
                .get(`/delivery-history/shipment/${shipmentId}`)
                .catch(() => ({ data: [] }));

            setOrder(loadedOrder);
            setHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to load order");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOrder();
    }, [id]);

    if (loading) return <p>Loading...</p>;
    if (error) return <p className="text-red-600">{error}</p>;
    if (!order) return <p>Order not found</p>;

    const shipment = order.shipment || {};
    const client = order.client || {};
    const legalEntity = order.legalEntity || order.company || {};

    const isLegalEntity =
        !!order.legalEntityId ||
        !!legalEntity.companyName ||
        order.userType === "LegalEntity" ||
        order.userType === 1;

    const shipmentStatus = shipment.status || "Created";
    const trackingNumber = shipment.trackingNumber || "—";
    const paymentStatus = order.paymentStatus || shipment.paymentStatus || "Pending";

    return (
        <div className="max-w-6xl mx-auto p-6 space-y-6">
            <h2 className="text-2xl font-bold">
                Order #{order.orderNumber || order.id}
            </h2>

            <div className="border rounded p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
                <div>
                    <p><b>Status:</b> {shipmentStatus}</p>
                    <p><b>Payment:</b> {paymentStatus}</p>
                    <p><b>Tracking:</b> {trackingNumber}</p>
                    <p><b>Amount:</b> {order.totalAmount} {order.currency}</p>
                </div>

                <div>
                    <p><b>Client type:</b> {isLegalEntity ? "Legal Entity" : "Individual Client"}</p>
                    <p><b>Created:</b> {order.createdAt ? new Date(order.createdAt).toLocaleString() : "—"}</p>
                    <p><b>Source:</b> {order.source || "Web"}</p>
                    <p><b>External:</b> {order.externalOrderNumber || "—"}</p>
                </div>
            </div>

            <div className="border rounded p-4 bg-white">
                <h3 className="font-semibold mb-3">
                    {isLegalEntity ? "Legal Entity Information" : "Individual Client Information"}
                </h3>

                {isLegalEntity ? (
                    <div className="space-y-1">
                        <p><b>Company:</b> {legalEntity.companyName || "—"}</p>
                        <p><b>Tax ID:</b> {legalEntity.taxId || "—"}</p>
                        <p><b>Registration:</b> {legalEntity.registrationNumber || "—"}</p>
                        <p><b>Contact:</b> {legalEntity.contactPerson || "—"}</p>
                        <p><b>Phone:</b> {legalEntity.companyPhone || legalEntity.phoneNumber || "—"}</p>
                    </div>
                ) : (
                    <div className="space-y-1">
                        <p><b>Client ID:</b> {order.clientId || client.id || "—"}</p>
                        <p><b>Name:</b> {client.name || "—"}</p>
                        <p><b>Last Name:</b> {client.lastName || "—"}</p>
                        <p><b>Email:</b> {client.email || "—"}</p>
                        <p><b>Phone:</b> {client.phoneNumber || client.phone || "—"}</p>
                    </div>
                )}
            </div>

            <div className="border rounded p-4 bg-white">
                <div className="flex justify-between items-center">
                    <h3 className="font-semibold">Items</h3>

                    <button
                        className="bg-indigo-600 text-white px-3 py-1 rounded"
                        onClick={() => setShowAddItem(true)}
                    >
                        + Add item
                    </button>
                </div>

                {order.items?.length ? (
                    <ul className="mt-2 space-y-1">
                        {order.items.map((item) => (
                            <li key={item.id}>
                                {item.name} × {item.quantity}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-gray-500 mt-2">No items yet</p>
                )}
            </div>

            <OrderTimeline status={shipmentStatus} />

            <div className="border rounded p-4 bg-white">
                <h3 className="font-semibold mb-3">Delivery History</h3>

                {history.length === 0 ? (
                    <p className="text-gray-500">No history records.</p>
                ) : (
                    history.map((h) => (
                        <div key={h.id} className="border-b py-2">
                            <p><b>Status:</b> {h.status || "—"}</p>
                            <p><b>Date:</b> {h.createdAt ? new Date(h.createdAt).toLocaleString() : "—"}</p>
                        </div>
                    ))
                )}
            </div>

            {showAddItem && (
                <AddItemModal
                    shipmentId={shipment.id || order.shipmentId || order.id}
                    onClose={() => setShowAddItem(false)}
                    onAdded={async () => {
                        setShowAddItem(false);
                        await loadOrder();
                    }}
                />
            )}
        </div>
    );
}